import { useEffect, useState, useMemo } from "react";
import { Pagination, Navigation } from "swiper";
import NextImage from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import ArrowLeft from "assets/imgs/left-arrow.svg";
import ArrowRight from "assets/imgs/right-arrow.svg";
import ResourceCard from "./ResourceCard";
import formatDollar from "utils/formatDollar";
import Grid from "elements/Grid";
import "swiper/css/bundle";
import { CMS, ContentfulEntryType } from "utils/types";

function getResourceHref(val: CMS.CMSResource): string {
	if (val.tags.includes("Case Studies")) {
		return val.tags.includes("Commercial")
			? `/commercial-case-study/${encodeURIComponent(val.slug)}`
			: `/case-study/${encodeURIComponent(val.slug)}`;
	}
	return val.tags.includes("Insights")
		? `/insight/${encodeURIComponent(val.slug)}`
		: `/blog/${encodeURIComponent(val.slug)}`;
}

function ResourceSlides(data: CMS.CMSResource[], type: ContentfulEntryType) {
	return data.map(val => {
		const id = val.slug;
		const imgUrl = val.heroImage.fields.file.url;
		const savings = (
			val as CMS.CommercialCaseStudyFields | CMS.ResidentialCaseStudyFields
		).taxBillSavings
			? formatDollar((val as CMS.CommercialCaseStudyFields).taxBillSavings)
			: "";
		const publishedAt = val.createdAt;
		const description = val.subtitle;
		const href = getResourceHref(val);

		return (
			<SwiperSlide key={id}>
				<ResourceCard
					type={type}
					id={id}
					href={href}
					img={imgUrl}
					savings={savings}
					date={publishedAt}
					title={val.title}
					description={description}
				/>
			</SwiperSlide>
		);
	});
}

export default function ResourceSlider({
	resourceData,
	type,
	swiperID,
}: {
	resourceData: CMS.CMSResource[];
	type: ContentfulEntryType;
	swiperID: string;
}) {
	const [renderCarousel, setRenderCarousel] = useState(false);

	const slides = useMemo(
		() => ResourceSlides(resourceData, type),
		[resourceData, type]
	);

	useEffect(() => {
		setRenderCarousel(true);
	}, []);

	return (
		<div id="resource-cards-slider">
			<Grid>
				{renderCarousel ? (
					<>
						<div className="resources-more-cards">
							<div
								className="card-slider-nav-prev"
								id={`card-slider-nav-prev-${swiperID}`}>
								<ArrowLeft />
							</div>
							<div className="outer-swiper-wrapper">
								{renderCarousel && (
									<Swiper
										spaceBetween={16}
										observer
										resizeObserver
										observeParents
										// loop={!isDesktop}
										breakpoints={{
											336: {
												slidesPerView: 1,
											},
											672: {
												slidesPerView: 2,
											},
											960: {
												slidesPerView: 3,
											},
										}}
										pagination={{
											el: `#card-slider-pagination-${swiperID}`,
											clickable: true,
											renderBullet: function (_, className) {
												return (
													'<span class="' +
													className +
													' card-pagination-bullet">' +
													"</span>"
												);
											},
										}}
										modules={[Pagination, Navigation]}
										navigation={{
											nextEl: `#card-slider-nav-next-${swiperID}`,
											prevEl: `#card-slider-nav-prev-${swiperID}`,
										}}>
										{slides}
									</Swiper>
								)}
							</div>
							<div
								className="card-slider-nav-next"
								id={`card-slider-nav-next-${swiperID}`}>
								<ArrowRight />
							</div>
							<div
								className="card-slider-pagination"
								id={`card-slider-pagination-${swiperID}`}
							/>
						</div>
					</>
				) : (
					<div className="casestudies-commercial-spinner">
						<NextImage
							alt="Loading spinner"
							src="/spinner.gif"
							width={100}
							height={100}
							style={{
								maxWidth: "100%",
								height: "auto",
							}}
						/>
					</div>
				)}
			</Grid>
		</div>
	);
}
